<template>
  <div
    class="modal fade fixed-end"
    tabindex="-1"
    role="dialog"
  >
    <div
      class="modal-dialog modal-dialog-vertical dw-md-8 dw-lg-6 dw-xl-5"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="drawer-title">{{ title }}</h2>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body">
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="control-label">First Name</label>
              <input
                name="first_name"
                v-model="newLead.first_name"
                type="text"
                class="form-control"
                data-vv-validate-on="input|blur"
                data-vv-as="first name"
                v-validate="'required'"
              />
              <help-block field="first_name" />
            </div>

            <div class="col-md-6 mb-3">
              <label class="control-label">Surname</label>
              <input
                name="surname"
                v-model="newLead.surname"
                type="text"
                class="form-control"
                data-vv-validate-on="input|blur"
                data-vv-as="surname"
                v-validate="'required'"
              />
              <help-block field="surname" />
            </div>

            <div class="col-md-6 mb-3">
              <label class="control-label">Phone</label>
              <input
                name="phone"
                v-model="newLead.phone"
                type="text"
                class="form-control"
                data-vv-validate-on="input|blur"
                data-vv-as="phone"
                v-validate="'required'"
              />
              <help-block field="phone" />
            </div>

            <div class="col-md-6 mb-3">
              <label class="control-label">Company</label>
              <input
                name="company"
                v-model="newLead.company"
                type="text"
                class="form-control"
              />
            </div>
          </div>

          <div class="mb-3">
            <label class="control-label">Email</label>
            <input
              name="email"
              v-model="newLead.email"
              type="text"
              class="form-control"
              data-vv-validate-on="input|blur"
              data-vv-as="email"
              v-validate="'required'"
            />
            <help-block field="email" />
          </div>

          <div class="mb-3">
            <label class="control-label">Message</label>
            <textarea
              name="message"
              v-model="newLead.message"
              class="form-control"
              data-vv-validate-on="input|blur"
              data-vv-as="message"
              v-validate="'required'"
              rows="5"
            ></textarea>
            <help-block field="message" />
          </div>

          <template v-if="newLead.id">
            <h3 class="h5 mt-2 mb-3">Dates</h3>
            <div class="row">
              <div v-for="field in dateFields" :key="field.key" class="col-md-6 mb-3">
                <label class="control-label">{{ field.label }}</label>
                <input
                  :name="field.key"
                  v-model="dates[field.key]"
                  type="date"
                  class="form-control"
                />
              </div>
            </div>
          </template>

          <div class="mb-3">
            <label class="control-label">Select Assigned</label>
            <div class="input-group">
              <span class="input-group-text"><i class="fal fa-fw fa-user"></i></span>
              <div class="flex-grow-1">
                <multiselect
                  :show-labels="false"
                  v-model="user"
                  :options="users"
                  label="name"
                  track-by="id"
                  placeholder="Select Assigned"
                  v-validate="'required'"
                  data-vv-name="user"
                  data-vv-as="assigned"
                  data-vv-validate-on="input"
                  :class="{ 'is-invalid': errors.has('user') }"
                  @select="userSelected"
                ></multiselect>
              </div>
            </div>
            <!-- Outside the input group so Bootstrap won't show it from a sibling .is-invalid -->
            <help-block field="user" class="d-block" />
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-default"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <c-button @click="handleSave" class="btn btn-rounded btn-primary">Save</c-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { notify } from 'utils'
import { cloneDeep, omit } from 'lodash'
import { mapActions, mapState, mapGetters } from 'vuex'
import { CModal } from '@chameleon/chameleon-vue'

export default {
  $_veeValidate: {
    validator: 'new'
  },

  extends: CModal,

  props: ['title', 'lead'],

  data () {
    return {
      newLead: {},
      user: null,
      dateFields: [
        { key: 'created_at', label: 'Received' },
        { key: 'qualified_at', label: 'Qualified' },
        { key: 'proposal_at', label: 'Proposal' },
        { key: 'won_at', label: 'Won' },
        { key: 'lost_at', label: 'Lost' }
      ],
      dates: {},
      originalDates: {}
    }
  },

  mounted () {
    this.getUsers()

    this.addEventListener('open', () => {
      this.newLead = cloneDeep(this.lead)
      this.user = this.newLead.user_id ? this.getUserById(this.newLead.user_id) : null

      this.dates = {}
      this.dateFields.forEach(({ key }) => {
        this.$set(this.dates, key, this.toDateInput(this.lead[key]))
      })
      this.originalDates = { ...this.dates }
    })
  },

  computed: {
    ...mapState('users', ['users']),
    ...mapGetters('users', { getUserById: 'getById' })
  },

  methods: {
    ...mapActions('lead', ['addSingle', 'updateSingle']),
    ...mapActions('users', ['getUsers']),

    userSelected (selected) {
      this.newLead.user_id = selected.id
    },

    toDateInput (value) {
      return value ? moment(value).format('YYYY-MM-DD') : null
    },

    /**
     * Only send dates the user changed so untouched ones keep their time of day.
     */
    updatePayload () {
      const dateKeys = this.dateFields.map(({ key }) => key)
      const payload = omit(this.newLead, dateKeys)

      dateKeys.forEach((key) => {
        if ((this.dates[key] || null) !== (this.originalDates[key] || null)) {
          payload[key] = this.dates[key] || null
        }
      })

      return payload
    },

    handleSave () {
      return this.newLead.id ? this.handleUpdate() : this.handleAdd()
    },

    async handleAdd () {
      if (!await this._validate()) {
        notify.error('Form validation failed, please check your inputs and try again.')
        return
      }

      return this.addSingle(this.newLead).then(() => {
        notify.success('Lead successfuly created.')
        this.$emit('saved')
        this.close()
      }).catch((error) => {
        if (error.response && error.response.status === 422) {
          notify.error('Form validation failed, please check your inputs and try again.')
        } else {
          console.error(error)
          notify.error('Failed to create lead, please try again.')
        }
      })
    },

    async handleUpdate () {
      if (!await this._validate()) {
        notify.error('Form validation failed, please check your inputs and try again.')
        return
      }

      return this.updateSingle({ id: this.newLead.id, payload: this.updatePayload() }).then(() => {
        notify.success('Lead successfuly updated.')
        this.$emit('saved')
        this.close()
      }).catch((error) => {
        if (error.response && error.response.status === 422) {
          notify.error('Form validation failed, please check your inputs and try again.')
        } else {
          console.error(error)
          notify.error('Failed to update lead, please try again.')
        }
      })
    }
  }
}
</script>

<style scoped>
.multiselect.is-invalid >>> .multiselect__tags {
  border-color: var(--bs-form-invalid-border-color, #c62b3b);
}
</style>
